import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import companyService from '../services/companyService';
import authService from '../services/authService';

const CompanyContext = createContext(null);

// Pricing fallbacks, used before settings load or if the request fails. These
// match the values that were previously hardcoded in AddProduct.jsx.
const DEFAULT_MARGE = 50; // %
const DEFAULT_TVA = 19;   // %

export function CompanyProvider({ children }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    // The endpoint requires authentication; skip the call on the login screen.
    if (!authService.isAuthenticated()) {
      setLoading(false);
      return null;
    }
    try {
      const data = await companyService.getSettings();
      setSettings(data);
      return data;
    } catch (err) {
      console.error('Failed to load company settings:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = {
    settings,
    loading,
    refresh,
    setSettings,
    // Convenience accessors with safe fallbacks (numbers).
    marge: settings && settings.marge_defaut != null ? Number(settings.marge_defaut) : DEFAULT_MARGE,
    tva: settings && settings.tva_defaut != null ? Number(settings.tva_defaut) : DEFAULT_TVA,
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompany() {
  const ctx = useContext(CompanyContext);
  if (!ctx) {
    return {
      settings: null,
      loading: false,
      refresh: () => {},
      setSettings: () => {},
      marge: DEFAULT_MARGE,
      tva: DEFAULT_TVA,
    };
  }
  return ctx;
}

export default CompanyContext;
